import React from "react";
import { useTranslation } from "react-i18next";
import {
  getUserInfoFromToken,
  getImpersonation,
  endImpersonation,
} from "../../store/action";
import { loadPage } from "../../utils/pageLoad";

/**
 * Leave a simulation and reload into the platform session it was opened from
 * — or the login page, if that session expired in the meantime.
 *
 * A full load rather than a client-side navigation: every piece of the
 * dashboard read the owner's token on mount, and a reload is the one way to be
 * sure none of it is still showing the business afterwards.
 */
export const leaveImpersonation = () => {
  const restored = endImpersonation();
  loadPage(restored ? "/dashboard/companies" : "/login");
};

/**
 * Says, on every dashboard page, that this is a SystemOwner acting as a
 * business's owner — and how to stop.
 *
 * Without it the simulation is indistinguishable from the owner's own session:
 * same menu, same email in the sidebar. Something created by mistake in the
 * wrong business is exactly the error this exists to prevent.
 *
 * Renders nothing on an ordinary token. Reads the simulation flag from the
 * token itself, not from the localStorage note beside it — the note only
 * supplies the business's name for the sentence.
 */
const ImpersonationBanner = () => {
  const { t } = useTranslation();

  if (getUserInfoFromToken()?.impersonatorId == null) return null;

  const impersonation = getImpersonation();
  const email = impersonation?.ownerEmail ?? getUserInfoFromToken()?.email;

  return (
    <div
      className="mb-5 flex flex-col gap-3 rounded-card border border-amber-300 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      role="region"
      aria-label={t("Dashboard.Impersonation.regionLabel")}
      data-testid="impersonation-banner"
    >
      <div className="flex min-w-0 items-start gap-2.5">
        <i
          className="bi bi-person-badge mt-0.5 text-amber-700"
          aria-hidden="true"
        />
        <div className="min-w-0">
          <p className="m-0 text-fluid-sm font-semibold break-words text-ink-900">
            {t("Dashboard.Impersonation.title", {
              business:
                impersonation?.businessName ??
                t("Dashboard.Impersonation.thisBusiness"),
            })}
          </p>
          <p className="m-0 mt-0.5 text-xs break-words text-ink-600">
            {t("Dashboard.Impersonation.description", { email })}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={leaveImpersonation}
        className="inline-flex min-h-11 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border border-amber-600 bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600"
      >
        <i className="bi bi-box-arrow-left" aria-hidden="true" />
        {t("Dashboard.Impersonation.exit")}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
